import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  DxDataGridModule,
  DxTextBoxModule,
  DxValidatorModule,
} from 'devextreme-angular';
import {
  MultiStepPopupComponent,
  Step,
} from 'src/app/components/multi-step-popup/multi-step-popup.component';
import { ContactService } from 'src/app/services/contact.service';
import { ProjectListService } from 'src/app/services/project-list.service';
import { AppGlobals } from 'src/app/app.globals';
import { GpdRoleSelectComponent } from 'src/app/components/gpd-role-select/gpd-role-select.component';
import { GpdPaginationComponent } from 'src/app/components/gpd-pagination/gpd-pagination.component';

interface SelectedProject {
  id: number;
  nr: string;
  title: string;
  role: string;
}

@Component({
    selector: 'app-contact-add-project-modal',
    imports: [
        CommonModule,
        DxDataGridModule,
        DxTextBoxModule,
        DxValidatorModule,
        MultiStepPopupComponent,
        GpdRoleSelectComponent,
        GpdPaginationComponent,
    ],
    templateUrl: './contact-add-project-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./contact-add-project-modal.component.css']
})
export class ContactAddProjectModalComponent implements OnChanges, AfterViewInit {
  @Input() visible = false;
  @Input() contactId!: number;
  @Input() contactType!: 'person' | 'company';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSaved = new EventEmitter<void>();

  @ViewChild('selectProjectStepTemplate') selectProjectStepTemplate!: TemplateRef<any>;
  @ViewChild('roleStepTemplate') roleStepTemplate!: TemplateRef<any>;
  @ViewChild(MultiStepPopupComponent) multiStepPopup?: MultiStepPopupComponent;

  steps: Step[] = [];

  // Captured from (onInitialized) — the grid lives inside a dxTemplate, so
  // @ViewChild does not reach it.
  projectGridInstance: any;

  projects: any[] = [];

  selectedProjectKeys: number[] = [];
  selectedProjects: SelectedProject[] = [];

  // Drives the hidden validator that gates step 1 until at least one project is selected.
  selectionValidatorValue = '';

  // Project grid paging — standard (non-virtual) scrolling + DevExtreme's own paging with
  // its default pager hidden, driven instead by <app-gpd-pagination> below the grid, same
  // pattern as the "Add Component" popup (project-info-components.component). This also
  // means the grid (and so the whole popup, height="auto") sizes to its actual content
  // instead of always reserving a fixed 82vh.
  projectGridCurrentPage = 1;
  projectGridTotalPages = 1;
  projectGridItemsPerPage = 0;
  projectGridTotalItems = 0;

  get projectGridShowingFrom(): number {
    if (this.projectGridTotalItems === 0) return 0;
    return this.projectGridItemsPerPage * (this.projectGridCurrentPage - 1) + 1;
  }

  get projectGridShowingTo(): number {
    return Math.min(this.projectGridItemsPerPage * this.projectGridCurrentPage, this.projectGridTotalItems);
  }

  private saving = false;
  private viewReady = false;

  constructor(
    private contactService: ContactService,
    private projectListService: ProjectListService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.buildSteps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reset();
      this.loadData();
      this.buildSteps();
    }
  }

  private buildSteps(): void {
    if (!this.viewReady || !this.selectProjectStepTemplate || !this.roleStepTemplate) {
      return;
    }

    this.steps = [
      {
        id: 'select-project',
        title: $localize`:@@selectProject:Select project`,
        icon: 'solar-panel',
        template: this.selectProjectStepTemplate,
      },
      {
        id: 'define-role',
        title: $localize`:@@defineRole:Define role`,
        icon: 'edit',
        template: this.roleStepTemplate,
      },
    ];

    this.cdr.detectChanges();
  }

  private reset(): void {
    this.selectedProjectKeys = [];
    this.selectedProjects = [];
    this.selectionValidatorValue = '';
    this.saving = false;
    // The popup content is not re-created between openings, so send it back to step 1.
    this.multiStepPopup?.reset();
  }

  private async loadData(): Promise<void> {
    try {
      const projectsRes = await this.projectListService.getProjectList({});
      this.projects = projectsRes?.projectList ?? [];

      this.cdr.detectChanges();
      // deferRendering is off, so the grid was created before the popup was
      // laid out and measured a zero-height viewport.
      setTimeout(() => {
        this.projectGridInstance?.updateDimensions();
      });
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  onProjectRowClick(e: any): void {
    if (e.rowType !== 'data') {
      return;
    }
    // Ignore clicks that land on the checkbox itself — DevExtreme already toggles those.
    if (e.event?.target?.closest?.('.dx-command-select')) {
      return;
    }
    const grid = e.component;
    if (grid.isRowSelected(e.key)) {
      grid.deselectRows([e.key]);
    } else {
      grid.selectRows([e.key], true);
    }
  }

  onProjectSelectionChanged(e: any): void {
    this.selectedProjectKeys = e.selectedRowKeys ?? [];
    this.selectionValidatorValue = this.selectedProjectKeys.length > 0 ? 'ok' : '';
    this.multiStepPopup?.triggerValidationUpdate();
  }

  getProjectGridPageInfo(): void {
    if (!this.projectGridInstance) {
      return;
    }
    this.projectGridCurrentPage = this.projectGridInstance.pageIndex() + 1;
    this.projectGridTotalPages = this.projectGridInstance.pageCount();
    this.projectGridItemsPerPage = this.projectGridInstance.pageSize();
    this.projectGridTotalItems = this.projectGridInstance.totalCount();
  }

  onProjectGridOptionChanged(e: any): void {
    if (e.fullName === 'paging.pageIndex' || e.fullName === 'paging.pageSize' || e.fullName === 'dataSource') {
      this.getProjectGridPageInfo();
    }
  }

  onProjectGridPageChange(page: number): void {
    if (this.projectGridInstance) {
      this.projectGridInstance.pageIndex(page - 1);
      this.getProjectGridPageInfo();
    }
  }

  onNextClicked(): void {
    const previousRoles = new Map(this.selectedProjects.map((p) => [p.id, p.role]));

    this.selectedProjects = this.selectedProjectKeys
      .map((id) => {
        const project = this.projects.find((p) => p.id === id);
        return project
          ? {
              id: project.id,
              nr: project.nr ?? '',
              title: project.title ?? '',
              role: previousRoles.get(id) ?? '',
            }
          : null;
      })
      .filter((p): p is SelectedProject => p !== null);
  }

  async onFinishClicked(): Promise<void> {
    if (this.saving || !this.selectedProjects.length) {
      return;
    }
    this.saving = true;

    // addProjectContact links one project to a batch of contacts — since we're going the
    // other way (one contact into several projects), it's called once per selected project.
    const results = await Promise.all(
      this.selectedProjects.map((project) =>
        this.contactService
          .addProjectContact({
            ProjectId: project.id,
            Contacts: [
              {
                ContactId: this.contactId,
                ContactType: this.contactType,
                Role: project.role,
              },
            ],
          })
          .catch((error) => ({ hasError: true, feedback: String(error) }))
      )
    );

    this.saving = false;

    const failed = results.find((r) => r?.hasError);
    if (failed) {
      AppGlobals.ShowErrorMessage(failed.feedback);
      return;
    }

    this.onSaved.emit();
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
